import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { CommonFunctionsService } from '../../../services/common-functions.service';
import { Constants } from '../../../constants/constants';
import { ReviewService } from '../../../services/review.service';
import { ReviewDetails } from '../../../models/ReviewDetails';
import { Subject, takeUntil } from 'rxjs';
import { CommonDataService } from '../../../services/common-data.service';
import { ExportTitleCaseMap } from '../../../models/PdfRelatedModels';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { ExportObjectAsFieldValuePDFOptions } from '../../../models/ExportObjectAsFieldValuePDFOptions';
import { SiRadioButtonComponent } from '../../Si-Web-Toolkit/si-radio-button/si-radio-button.component';
import { SiCheckBoxComponent } from '../../Si-Web-Toolkit/si-check-box/si-check-box.component';
import { MatRadioModule } from '@angular/material/radio';
import { FormControl, FormGroup, FormsModule, NonNullableFormBuilder, ReactiveFormsModule } from '@angular/forms';
import { SiFormFieldComponent } from '../../Si-Web-Toolkit/si-form-field/si-form-field.component';
import { MatLabel } from '@angular/material/select';
import { SiTextAreaComponent } from '../../Si-Web-Toolkit/si-text-area/si-text-area.component';
import { CostReviewBaseAttributes } from '../../../models/CostReviewBaseAttributes';
import { CostReviewBidDocumentationAvailable } from '../../../models/CostReviewBidDocumentationAvailable';
import { ReviewDetailTabsService } from '../../../lib/review-detail-tabs.service';
import { OptionalWorkflowDirective } from '../../../Directives/optional-workflow-directive';
import { TrackChangesDirective } from '../../../Directives/track-changes.directive';
import { CostReviewEstimateType } from '../../../public-api';
import { MultilingualCurrencyPipe } from '../../../pipes/multilingual-currency/multilingual-currency.pipe';

@Component({
  selector: 'app-sol-bid-documentation-tab',
  imports: [
    CommonModule,
    TranslatePipe,
    SiRadioButtonComponent,
    SiCheckBoxComponent,
    MatRadioModule,
    FormsModule,
    ReactiveFormsModule,
    SiFormFieldComponent,
    MatLabel,
    SiTextAreaComponent,
    OptionalWorkflowDirective,
    TrackChangesDirective,
    MultilingualCurrencyPipe
  ],
  templateUrl: './sol-bid-documentation-tab.component.html',
  styleUrl: './sol-bid-documentation-tab.component.scss'
})
export class SolBidDocumentationTabComponent implements OnInit, OnDestroy {
  destroy$ = new Subject<void>();
  constants = Constants;
  costReviewBidDocumentationAvailable: CostReviewBidDocumentationAvailable | undefined = new CostReviewBidDocumentationAvailable();
  business: string = "";
  isSalePriceGreaterThan150: boolean = false;
  proposalLink: string = '';
  opportunityLink: string = '';
  @Input() showProposalOpportunityLink: boolean = true;
  @Input() showSectionNotApplicableMsg: boolean = true;
  @Output() disablePdf = new EventEmitter<boolean>();
  selectedOption: string = 'No';
  isReadOnlyMode: boolean = false;
  costReviewBidDocumentationFormGroup! : FormGroup<{
  jobSpecifications: FormControl<string>;
  mechanicalDrawings: FormControl<string>;
  fireSystemElectricalDrawings: FormControl<string>;
  securitySystemElectricalDrawings: FormControl<string>;
  exceptionsClarifications: FormControl<string>;
  smokeControlSequence: FormControl<string>;
  scopeOfWorkLetter: FormControl<string>;
  productDiscount: FormControl<string>;
  projectContractAddenda: FormControl<string>;
  notes: FormControl<string>;
  listAllAddenda: FormControl<string>;
}>;

optionalWorkflowCondition: boolean = false;
isDisabled: boolean = false;

  constructor(private commonDataService: CommonDataService,
    private commonFunctionsService: CommonFunctionsService,
    private readonly reviewService: ReviewService,
    private translate: TranslateService,
    private readonly formBuilder: NonNullableFormBuilder,
    private readonly reviewDetailsTabService: ReviewDetailTabsService) { }


  ngOnInit() {
    this.business = this.reviewService.getBusiness();
     const estimateType = this.reviewService.convertToEstimateType(
      this.reviewDetailsTabService.getOptionalWorkflowAndEstimateType().optionalEstimateType
    );
    this.isSalePriceGreaterThan150 = estimateType === CostReviewEstimateType.EstimateGreater150K;
    this.getReviewLibraryState();
    this.createFormGroup();
    this.initializeBidDocumentationData();
    this.optionalWorkflowCondition = this.reviewDetailsTabService.getOptionalWorkflowAndEstimateType()?.optionalWorkflow || false;
    this.commonDataService.triggerTabMethod$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.exportToPdf(this.reviewService.selectedReview, Constants.SOL_BID_DOCUMENTATION);
      });
    this.sendDisablePdfEvent();
    this.setupReadOnlyMode();
    this.updateFormEditability();
    this.subscribeToReviewFormDataSyncRequest();
  }

  get isBidDocumentationChecked(): boolean {
    return !!this.costReviewBidDocumentationAvailable?.isBidDocumentationApplicable;
  }

  get isSectionVisible(): boolean {
    return !!this.costReviewBidDocumentationAvailable || (this.isBidDocumentationChecked && this.optionalWorkflowCondition);
  }

  get isSectionLocked(): boolean {
    return this.optionalWorkflowCondition && !this.isBidDocumentationChecked;
  }

  onBidDocumentationToggle(checked: boolean): void {
    if (!this.costReviewBidDocumentationAvailable) return;
    this.costReviewBidDocumentationAvailable.isBidDocumentationApplicable = checked;
    this.updateFormEditability();
  }

  private updateFormEditability(): void {
    if (!this.costReviewBidDocumentationFormGroup) return;
    const lockedByReadOnly = !this.reviewDetailsTabService.getIsLibraryUsedInSQ() && this.isReadOnlyMode;
    if (lockedByReadOnly || this.isSectionLocked) {
      this.costReviewBidDocumentationFormGroup.disable();
    } else {
      this.costReviewBidDocumentationFormGroup.enable();
    }
  }

  /** Allows the host app to force this tab to push its latest data without waiting for tab switch/destroy. */
  private subscribeToReviewFormDataSyncRequest(): void {
    this.reviewDetailsTabService.getReviewFormDataSyncRequested$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.pushFormDataToService());
  }

  private setupReadOnlyMode(): void {
    this.commonDataService.libraryReadOnlyMode$
      .pipe(takeUntil(this.destroy$))
      .subscribe((readOnly: boolean) => {
        const isLibraryUsedInSQ = this.reviewDetailsTabService.getIsLibraryUsedInSQ();
        if (isLibraryUsedInSQ) {
          this.isDisabled = readOnly;
          this.isReadOnlyMode = false;
        } else {
          this.isReadOnlyMode = readOnly;
        }
        this.updateFormEditability();
      });
    this.subscribeToDataRefresh();
  }

  private subscribeToDataRefresh(): void {
    this.reviewService.onDataRefreshed$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        const isLibraryUsedInSQ = this.reviewDetailsTabService.getIsLibraryUsedInSQ();
        this.isDisabled = isLibraryUsedInSQ ? this.commonDataService.libraryReadOnlyMode : this.isDisabled;
        this.isReadOnlyMode = isLibraryUsedInSQ ? false : this.commonDataService.libraryReadOnlyMode;
        this.updateFormEditability();
      });
  }

  createFormGroup() {
    this.costReviewBidDocumentationFormGroup = this.formBuilder.group({
      jobSpecifications: this.formBuilder.control(Constants.EMPTY_STRING),
      mechanicalDrawings: this.formBuilder.control(Constants.EMPTY_STRING),
      fireSystemElectricalDrawings: this.formBuilder.control(Constants.EMPTY_STRING),
      securitySystemElectricalDrawings: this.formBuilder.control(Constants.EMPTY_STRING),
      exceptionsClarifications: this.formBuilder.control(Constants.EMPTY_STRING),
      scopeOfWorkLetter: this.formBuilder.control(Constants.EMPTY_STRING),
      smokeControlSequence: this.formBuilder.control(Constants.EMPTY_STRING),
      productDiscount: this.formBuilder.control(Constants.EMPTY_STRING),
      projectContractAddenda: this.formBuilder.control(Constants.EMPTY_STRING),
      notes: this.formBuilder.control(Constants.EMPTY_STRING),
      listAllAddenda: this.formBuilder.control(Constants.EMPTY_STRING)
    });
  }

  ngOnDestroy() {
    this.pushFormDataToService();
    this.destroy$.next();
    this.destroy$.complete();
  }

  private pushFormDataToService(): void {
    if (!this.costReviewBidDocumentationFormGroup || !this.costReviewBidDocumentationAvailable) return;
    const formData = this.costReviewBidDocumentationFormGroup.getRawValue();

    this.costReviewBidDocumentationAvailable.jobSpecifications = this.getParameterCode(formData.jobSpecifications, this.costReviewBidDocumentationAvailable.jobSpecifications);
    this.costReviewBidDocumentationAvailable.mechanicalDrawings = this.getParameterCode(formData.mechanicalDrawings, this.costReviewBidDocumentationAvailable.mechanicalDrawings);
    this.costReviewBidDocumentationAvailable.fireSystemElectricalDrawings = this.getParameterCode(formData.fireSystemElectricalDrawings, this.costReviewBidDocumentationAvailable.fireSystemElectricalDrawings);
    this.costReviewBidDocumentationAvailable.securitySystemElectricalDrawings = this.getParameterCode(formData.securitySystemElectricalDrawings, this.costReviewBidDocumentationAvailable.securitySystemElectricalDrawings);
    this.costReviewBidDocumentationAvailable.exceptionsClarifications = this.getParameterCode(formData.exceptionsClarifications, this.costReviewBidDocumentationAvailable.exceptionsClarifications);
    this.costReviewBidDocumentationAvailable.smokeControlSequence = this.getParameterCode(formData.smokeControlSequence, this.costReviewBidDocumentationAvailable.smokeControlSequence);
    this.costReviewBidDocumentationAvailable.scopeOfWorkLetter = this.getParameterCode(formData.scopeOfWorkLetter, this.costReviewBidDocumentationAvailable.scopeOfWorkLetter);
    this.costReviewBidDocumentationAvailable.productDiscount = this.getParameterCode(formData.productDiscount, this.costReviewBidDocumentationAvailable.productDiscount);

    // Direct string fields
    this.costReviewBidDocumentationAvailable.projectContractAddenda = formData.projectContractAddenda;
    this.costReviewBidDocumentationAvailable.notes = formData.notes;
    this.costReviewBidDocumentationAvailable.listAllAddenda = formData.listAllAddenda;
  }

  initializeBidDocumentationData() {
    this.isSalePriceGreaterThan150 = this.reviewDetailsTabService.estimateSellPrice > this.constants.ONE_LAKH_FIFTY_THOUSAND;
    let data: CostReviewBaseAttributes = this.reviewService.costReviewDetails.reviewObject;
    let newCostReviewObject = this.reviewService.newCostReviewObject;
    // this.business = this.reviewService.getBusiness();
    this.proposalLink = newCostReviewObject.proposalLink || Constants.EMPTY_STRING;
    this.opportunityLink = newCostReviewObject.opportunityLink || Constants.EMPTY_STRING;
    this.costReviewBidDocumentationAvailable = data?.costReviewBidDocumentationAvailable;
    this.mapBidDocumentationData();
  }

  mapBidDocumentationData() {
    if (this.costReviewBidDocumentationAvailable === undefined || this.costReviewBidDocumentationAvailable === null) {
      return;
    }

    this.costReviewBidDocumentationFormGroup.patchValue({
      jobSpecifications: this.getParameterValues(this.constants.YES_NO, this.costReviewBidDocumentationAvailable.jobSpecifications),
      mechanicalDrawings: this.getParameterValues(this.constants.YES_NO, this.costReviewBidDocumentationAvailable.mechanicalDrawings),
      fireSystemElectricalDrawings: this.getParameterValues(this.constants.YES_NO, this.costReviewBidDocumentationAvailable.fireSystemElectricalDrawings),
      securitySystemElectricalDrawings: this.getParameterValues(this.constants.YES_NO, this.costReviewBidDocumentationAvailable.securitySystemElectricalDrawings),
      exceptionsClarifications: this.getParameterValues(this.constants.YES_NO, this.costReviewBidDocumentationAvailable.exceptionsClarifications),
      smokeControlSequence: this.getParameterValues(this.constants.YES_NO, this.costReviewBidDocumentationAvailable.smokeControlSequence),
      scopeOfWorkLetter: this.getParameterValues(this.constants.YES_NO, this.costReviewBidDocumentationAvailable.scopeOfWorkLetter),
      productDiscount: this.getParameterValues(this.constants.YES_NO, this.costReviewBidDocumentationAvailable.productDiscount),
      projectContractAddenda: this.costReviewBidDocumentationAvailable.projectContractAddenda,
      notes: this.costReviewBidDocumentationAvailable.notes,
      listAllAddenda: this.costReviewBidDocumentationAvailable.listAllAddenda
    });
  }

  getParameterValues(key: string, value: string | number | undefined): string {
    return this.reviewService.getCostReviewParametersDescription(key, value);
  }

  private getParameterCode(description: string, currentValue: number): number {
    const code = this.reviewService.getCostReviewParametersCode(this.constants.YES_NO, description);
    return code !== undefined ? code : currentValue;
  }

  isSectionNotApplicableShow(): boolean {
    return !this.isSectionVisible && (this.business === this.constants.BUSINESS_FIRE || this.business === this.constants.BUSINESS_SECURITY || this.business === this.constants.BUSINESS_ELECTRICAL || this.business === this.constants.BUSINESS_AUTOMATION || this.business === this.constants.BUSINESS_MECHANICAL) && !this.showSectionNotApplicableMsg;
  }


  private exportBidDocumentationPdf(
    data: any,
    title: string,
    serviceReview: ReviewDetails
  ) {
    const options: ExportObjectAsFieldValuePDFOptions = {
      data,
      title,
      fileName: 'bid-documentation.pdf',
      estimateID: serviceReview.estimateID,
      estimateName: serviceReview.request,
      tabName: this.translate.instant('REVIEW_SOL_TABS_LABELS.BID_DOCUMENTATION'),
    };
    this.commonFunctionsService.exportObjectAsFieldValuePDF(options);
  }

  exportToPdf(serviceReview: ReviewDetails, tabName: string) {
    if (tabName !== this.constants.SOL_BID_DOCUMENTATION) return;

    if (this.shouldShowNoData()) {
      this.exportBidDocumentationPdf(
        { [this.translate.instant('REVIEW_SOL_BID_DOCUMENTATION_TAB.BID_DOCUMENTATION_DATA_NOT_FOUND')]: this.constants.EMPTY_STRING },
        this.constants.EMPTY_STRING,
        serviceReview
      );
      return;
    }

    const exportData = this.buildExportData();

    this.exportBidDocumentationPdf(
      exportData,
      this.translate.instant('REVIEW_SOL_BID_DOCUMENTATION_TAB.TITLE'),
      serviceReview
    );
  }

  shouldShowNoData(): boolean {
    return !this.isSectionVisible || this.isSectionLocked;
  }

  buildExportData() {
    const fields = [
      { key: this.translate.instant('REVIEW_SOL_BID_DOCUMENTATION_TAB.JOB_SPECIFICATIONS'), value: this.getParameterValues(this.constants.YES_NO, this.costReviewBidDocumentationAvailable?.jobSpecifications) },
      { key: this.translate.instant('REVIEW_SOL_BID_DOCUMENTATION_TAB.MECHANICAL_DRAWINGS'), value: this.business !== this.constants.BUSINESS_SECURITY ? this.getParameterValues(this.constants.YES_NO, this.costReviewBidDocumentationAvailable?.mechanicalDrawings) : undefined },
      { key: this.translate.instant('REVIEW_SOL_BID_DOCUMENTATION_TAB.FIRE_SYSTEM_ELECTRICAL_DRAWINGS'), value: this.business === this.constants.BUSINESS_FIRE ? this.getParameterValues(this.constants.YES_NO, this.costReviewBidDocumentationAvailable?.fireSystemElectricalDrawings) : undefined },
      { key: this.translate.instant('REVIEW_SOL_BID_DOCUMENTATION_TAB.SECURITY_SYSTEM_ELECTRICAL_DRAWINGS'), value: this.business === this.constants.BUSINESS_SECURITY ? this.getParameterValues(this.constants.YES_NO, this.costReviewBidDocumentationAvailable?.securitySystemElectricalDrawings) : undefined },
      { key: this.translate.instant('REVIEW_SOL_BID_DOCUMENTATION_TAB.EXCEPTIONS_CLARIFICATIONS'), value: this.getParameterValues(this.constants.YES_NO, this.costReviewBidDocumentationAvailable?.exceptionsClarifications) },
      { key: this.translate.instant('REVIEW_SOL_BID_DOCUMENTATION_TAB.SCOPE_OF_WORK_LETTER'), value: this.getParameterValues(this.constants.YES_NO, this.costReviewBidDocumentationAvailable?.scopeOfWorkLetter) },
      { key: this.translate.instant('REVIEW_SOL_BID_DOCUMENTATION_TAB.PROJECT_CONTRACT_ADDENDA'), value: this.costReviewBidDocumentationAvailable?.projectContractAddenda },
      { key: this.translate.instant('REVIEW_SOL_BID_DOCUMENTATION_TAB.SMOKE_CONTROL_SEQUENCE'), value: this.business === this.constants.BUSINESS_FIRE ? this.getParameterValues(this.constants.YES_NO, this.costReviewBidDocumentationAvailable?.smokeControlSequence) : undefined },
      { key: this.translate.instant('REVIEW_SOL_BID_DOCUMENTATION_TAB.PRODUCT_PROMOTION_RECEIVED'), value: (this.business !== this.constants.BUSINESS_SECURITY && this.costReviewBidDocumentationAvailable?.producPromotionReceived !== this.constants.EMPTY_STRING) ? this.costReviewBidDocumentationAvailable?.producPromotionReceived : undefined },
      { key: this.translate.instant('REVIEW_SOL_BID_DOCUMENTATION_TAB.PRODUCT_DISCOUNT_APPLIED'), value: this.business === this.constants.BUSINESS_SECURITY ? this.getParameterValues(this.constants.YES_NO, this.costReviewBidDocumentationAvailable?.productDiscount) : undefined },
      {
        key: this.translate.instant('REVIEW_SOL_BID_DOCUMENTATION_TAB.SUBSIDY_RECEIVED'),
        value: (this.business === this.constants.BUSINESS_SECURITY || this.business === this.constants.BUSINESS_FIRE || this.isSalePriceGreaterThan150)
          ? this.formatDollar(this.costReviewBidDocumentationAvailable?.subsidyReceived)
          : undefined
      }, { key: this.translate.instant('REVIEW_SOL_BID_DOCUMENTATION_TAB.NOTES_OTHER'), value: (this.business === this.constants.BUSINESS_SECURITY || this.business === this.constants.BUSINESS_FIRE || this.isSalePriceGreaterThan150) ? this.costReviewBidDocumentationAvailable?.notes : undefined },
      { key: this.translate.instant('REVIEW_SOL_BID_DOCUMENTATION_TAB.LIST_ALL_ADDENDA'), value: (this.business !== this.constants.BUSINESS_FIRE && this.business !== this.constants.BUSINESS_AUTOMATION && this.business !== this.constants.BUSINESS_ELECTRICAL && this.business !== this.constants.BUSINESS_MECHANICAL) ? this.costReviewBidDocumentationAvailable?.listAllAddenda : undefined }
    ];

    const exportData: ExportTitleCaseMap = {};
    fields.filter(f => f.value !== undefined).forEach(f => exportData[f.key] = f.value !== undefined ? String(f.value) : undefined);

    if (this.proposalLink) {
      exportData['Proposal Link'] = this.proposalLink;
    }
    if (this.opportunityLink) {
      exportData['Opportunity Link'] = this.opportunityLink;
    }

    return exportData;
  }

  sendDisablePdfEvent() {
    this.disablePdf.emit(false);
  }

  formatDollar(value: string | number | undefined): string {
    if (value === undefined || value === null || value === Constants.EMPTY_STRING) return Constants.EMPTY_STRING;
    const num = typeof value === 'number' ? value : parseFloat(value);
    if (isNaN(num)) return String(value);
    return `$${num.toLocaleString('en-US', { minimumFractionDigits: Constants.TWO, maximumFractionDigits: Constants.TWO })}`;
  }

  private getReviewLibraryState(): void {
    this.isDisabled = this.reviewDetailsTabService.getReviewLibraryDisableFlag();
  }

}
